import { FirebaseError } from "firebase/app";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { getFirebaseServices } from "./firebase";
import type { MediaImage } from "./terra-boa-data";
import type { TerraBoaSession } from "./terra-boa-repository";

export const MAX_LISTING_IMAGES = 5;
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const ACCEPTED_IMAGE_LABEL = "JPG, PNG ou WebP";

const MAX_SOURCE_BYTES = 10 * 1024 * 1024;
const MAX_OUTPUT_BYTES = 2 * 1024 * 1024;

export type MediaVariant = "listing" | "avatar";

export type MediaDraft = MediaImage & {
  blob?: Blob;
  sourceName?: string;
};

export type MediaCommit = {
  images: MediaImage[];
  uploadedPaths: string[];
  removedPaths: string[];
};

export class MediaValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MediaValidationError";
  }
}

function mediaUploadError(error: unknown) {
  const code = error instanceof FirebaseError ? error.code : error instanceof Error && "code" in error ? String(error.code) : "";
  if (code.includes("unauthenticated")) return new MediaValidationError("Sua sessão expirou. Entre novamente antes de enviar imagens.");
  if (code.includes("unauthorized")) return new MediaValidationError("O Storage não autorizou o envio. Atualize a página, entre novamente e tente outra vez.");
  if (code.includes("quota-exceeded")) return new MediaValidationError("O espaço de imagens da plataforma está temporariamente indisponível.");
  if (code.includes("retry-limit-exceeded") || code.includes("network-request-failed")) return new MediaValidationError("A conexão falhou durante o envio da imagem. Verifique sua internet e tente novamente.");
  if (code.includes("canceled")) return new MediaValidationError("O envio da imagem foi cancelado antes da conclusão.");
  return error instanceof MediaValidationError ? error : new MediaValidationError("Não foi possível enviar a imagem ao Storage. Tente entrar novamente ou escolha outro arquivo.");
}

async function refreshStorageSession(session: TerraBoaSession & { uid: string }) {
  const services = getFirebaseServices();
  if (!services?.storage) throw new MediaValidationError("O Firebase Storage ainda não foi configurado para este projeto.");
  if (!services.auth.currentUser || services.auth.currentUser.uid !== session.uid) {
    throw new MediaValidationError("Sua sessão expirou. Entre novamente antes de enviar imagens.");
  }
  try {
    await services.auth.currentUser.getIdToken(true);
  } catch (error) {
    throw mediaUploadError(error);
  }
}

function createMediaId() {
  return globalThis.crypto?.randomUUID?.() ?? `img-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function cleanImage(image: MediaImage): MediaImage {
  return {
    id: image.id,
    url: image.url,
    ...(image.storagePath ? { storagePath: image.storagePath } : {}),
    ...(image.alt ? { alt: image.alt } : {}),
  };
}

function isInlineImage(image: MediaDraft) {
  return image.url.startsWith("data:image/");
}

async function inlineImageBlob(image: MediaDraft) {
  if (image.blob) return image.blob;
  if (!isInlineImage(image)) return null;

  try {
    const response = await fetch(image.url);
    const blob = await response.blob();
    if (!ACCEPTED_IMAGE_TYPES.includes(blob.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
      throw new MediaValidationError("A imagem antiga possui um formato não compatível. Escolha uma nova imagem para continuar.");
    }
    if (blob.size > MAX_OUTPUT_BYTES) {
      throw new MediaValidationError("A imagem antiga é grande demais para ser migrada. Escolha uma nova imagem para continuar.");
    }
    return blob;
  } catch (error) {
    throw error instanceof MediaValidationError
      ? error
      : new MediaValidationError("Não foi possível preparar a imagem antiga para o envio. Escolha uma nova imagem e tente novamente.");
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new MediaValidationError("Não foi possível preparar a imagem selecionada."));
    reader.readAsDataURL(blob);
  });
}

async function loadImage(file: File): Promise<HTMLImageElement> {
  const sourceUrl = await blobToDataUrl(file);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new MediaValidationError(`O arquivo “${file.name}” não pôde ser lido como imagem.`));
    image.src = sourceUrl;
  });
}

function encodeCanvas(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new MediaValidationError("Não foi possível reduzir a imagem selecionada.")),
      "image/webp",
      quality,
    );
  });
}

async function compressImage(file: File, variant: MediaVariant): Promise<Blob> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
    throw new MediaValidationError(`“${file.name}” não é compatível. Use ${ACCEPTED_IMAGE_LABEL}.`);
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new MediaValidationError(`“${file.name}” ultrapassa 10 MB.`);
  }

  const source = await loadImage(file);
  const longestSide = variant === "avatar" ? 900 : 1600;
  const scale = Math.min(1, longestSide / Math.max(source.naturalWidth, source.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(source.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(source.naturalHeight * scale));
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new MediaValidationError("Este navegador não conseguiu processar a imagem.");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, 0, canvas.width, canvas.height);

  let result = await encodeCanvas(canvas, 0.82);
  if (result.size > MAX_OUTPUT_BYTES) result = await encodeCanvas(canvas, 0.68);
  if (result.size > MAX_OUTPUT_BYTES) result = await encodeCanvas(canvas, 0.54);
  if (result.size > MAX_OUTPUT_BYTES) {
    throw new MediaValidationError(`“${file.name}” continuou muito grande após a redução. Escolha uma imagem menor.`);
  }
  return result;
}

export async function prepareImageFiles(files: FileList | File[], variant: MediaVariant): Promise<MediaDraft[]> {
  const prepared: MediaDraft[] = [];
  for (const file of Array.from(files)) {
    const blob = await compressImage(file, variant);
    prepared.push({
      id: createMediaId(),
      url: await blobToDataUrl(blob),
      blob,
      sourceName: file.name,
    });
  }
  return prepared;
}

function extensionFor(blob: Blob) {
  if (blob.type === "image/png") return "png";
  if (blob.type === "image/jpeg") return "jpg";
  return "webp";
}

async function uploadDraft(
  session: TerraBoaSession & { uid: string },
  storagePath: string,
  draft: MediaDraft,
  customMetadata: Record<string, string>,
): Promise<MediaImage> {
  const services = getFirebaseServices();
  if (!services?.storage) {
    throw new MediaValidationError("O Firebase Storage ainda não foi configurado para este projeto.");
  }
  if (!draft.blob) return cleanImage(draft);
  const objectRef = ref(services.storage, storagePath);
  await uploadBytes(objectRef, draft.blob, {
    contentType: draft.blob.type,
    cacheControl: "public,max-age=31536000,immutable",
    customMetadata,
  });
  return {
    id: draft.id,
    url: await getDownloadURL(objectRef),
    storagePath,
    ...(draft.alt ? { alt: draft.alt } : {}),
  };
}

export async function removeStoredMedia(session: TerraBoaSession, paths: string[]) {
  if (session.mode !== "firebase" || !paths.length) return;
  const storage = getFirebaseServices()?.storage;
  if (!storage) return;
  await Promise.allSettled(paths.map((path) => deleteObject(ref(storage, path))));
}

export async function commitListingMedia(
  session: TerraBoaSession,
  listingId: string,
  profileId: string,
  drafts: MediaDraft[],
  previous: MediaImage[],
  title: string,
): Promise<MediaCommit> {
  const selected = drafts.slice(0, MAX_LISTING_IMAGES);
  const previousPaths = previous.flatMap((image) => image.storagePath ? [image.storagePath] : []);
  const retainedPaths = new Set(selected.flatMap((image) => image.storagePath ? [image.storagePath] : []));
  const removedPaths = previousPaths.filter((path) => !retainedPaths.has(path));

  if (session.mode === "local") {
    return {
      images: selected.map((image, index) => cleanImage({ ...image, alt: index === 0 ? `${title} — imagem de capa` : `${title} — imagem ${index + 1}` })),
      uploadedPaths: [],
      removedPaths: [],
    };
  }
  if (!session.uid) throw new MediaValidationError("Faça login novamente antes de enviar imagens.");

  const uploadedPaths: string[] = [];
  const images: MediaImage[] = [];
  try {
    if (selected.some((draft) => Boolean(draft.blob) || isInlineImage(draft))) {
      await refreshStorageSession(session as TerraBoaSession & { uid: string });
    }
    for (let index = 0; index < selected.length; index += 1) {
      const draft = selected[index];
      const alt = index === 0 ? `${title} — imagem de capa` : `${title} — imagem ${index + 1}`;
      const blob = await inlineImageBlob(draft);
      if (!blob) {
        images.push(cleanImage({ ...draft, alt }));
        continue;
      }
      const storagePath = `listing-images/${session.uid}/${listingId}/${draft.id}.${extensionFor(blob)}`;
      images.push(await uploadDraft(session as TerraBoaSession & { uid: string }, storagePath, { ...draft, blob, alt }, { listingId, profileId }));
      uploadedPaths.push(storagePath);
    }
  } catch (error) {
    await removeStoredMedia(session, uploadedPaths);
    throw mediaUploadError(error);
  }
  return { images, uploadedPaths, removedPaths };
}

export async function commitProfileAvatar(
  session: TerraBoaSession,
  profileId: string,
  draft: MediaDraft,
  previous?: MediaImage,
): Promise<{ avatar: MediaImage; uploadedPaths: string[]; removedPaths: string[] }> {
  const avatarDraft = { ...draft, alt: `Foto do perfil` };
  const removedPaths = previous?.storagePath && previous.storagePath !== draft.storagePath ? [previous.storagePath] : [];
  if (session.mode === "local") {
    return { avatar: cleanImage(avatarDraft), uploadedPaths: [], removedPaths: [] };
  }
  if (!session.uid) throw new MediaValidationError("Faça login novamente antes de enviar a foto.");
  if (!draft.blob) return { avatar: cleanImage(avatarDraft), uploadedPaths: [], removedPaths };

  const storagePath = `profile-images/${session.uid}/avatar/${draft.id}.${extensionFor(draft.blob)}`;
  try {
    await refreshStorageSession(session as TerraBoaSession & { uid: string });
    const avatar = await uploadDraft(session as TerraBoaSession & { uid: string }, storagePath, avatarDraft, { profileId });
    return { avatar, uploadedPaths: [storagePath], removedPaths };
  } catch (error) {
    await removeStoredMedia(session, [storagePath]);
    throw mediaUploadError(error);
  }
}

export async function commitCampaignCover(session: TerraBoaSession, campaignId: string, draft: MediaDraft): Promise<{ image: MediaImage; uploadedPaths: string[] }> {
  if (session.mode === "local") return { image: cleanImage(draft), uploadedPaths: [] };
  if (!session.uid) throw new MediaValidationError("Faça login antes de enviar a capa.");
  if (!draft.blob) return { image: cleanImage(draft), uploadedPaths: [] };
  const storagePath = `campaign-images/${session.uid}/${campaignId}/${draft.id}.${extensionFor(draft.blob)}`;
  try {
    await refreshStorageSession(session as TerraBoaSession & { uid: string });
    const image = await uploadDraft(session as TerraBoaSession & { uid: string }, storagePath, draft, { campaignId });
    return { image, uploadedPaths: [storagePath] };
  } catch (error) {
    await removeStoredMedia(session, [storagePath]);
    throw mediaUploadError(error);
  }
}
